import { CartLinesRemoveMutation, CartLinesUpdateMutation } from 'gql/cart'
import PropTypes from 'prop-types'
import { useMutation } from 'urql'

import { formatMoney, isBlank, isPresent } from 'utils'

import { useCart } from 'hooks'

import ProductCardSmall from 'components/ProductCardSmall'
import Spacer from 'components/Spacer'
import Text from 'components/Text'

import {
  PriceCell,
  ProductCell,
  QuantityButtons,
  QuantityCell,
  QuantityDecrementButton,
  QuantityIncrementButton,
  Root,
} from './CartLine.style'

const CartLine = ({ line }) => {
  const { cart } = useCart()
  const [cartLinesUpdateResult, cartLinesUpdate] = useMutation(CartLinesUpdateMutation)
  const [cartLinesRemoveResult, cartLinesRemove] = useMutation(CartLinesRemoveMutation)

  const fetching = cartLinesUpdateResult.fetching || cartLinesRemoveResult.fetching

  if (isBlank(line)) return null

  const product = line.merchandise.product
  const { title, handle, vendor, tags } = product
  const namedTags = isPresent(tags) ? Object.fromEntries(tags.map((tag) => tag.split(':'))) : {}
  const artisan = namedTags.displayBrand || vendor
  const imageUrl = line.merchandise.image.url
  const price = line.merchandise.priceV2.amount
  const compareAtPrice = line.merchandise.compareAtPriceV2?.amount

  const decrementQuantity = async () => {
    await cartLinesUpdate({ cartId: cart.id, lines: [{ id: line.id, quantity: line.quantity - 1 }] })
  }

  const incrementQuantity = async () => {
    await cartLinesUpdate({ cartId: cart.id, lines: [{ id: line.id, quantity: line.quantity + 1 }] })
  }

  const remove = async () => {
    await cartLinesRemove({ cartId: cart.id, lineIds: [line.id] })
  }

  const selectedOptions = line.merchandise.selectedOptions
    .filter(({ value }) => value !== 'Default Title')
    ?.map(({ value }) => value)
    ?.join(' / ')

  return (
    <Root>
      <ProductCell>
        <ProductCardSmall
          handle={handle}
          imageUrl={imageUrl}
          artisan={artisan}
          title={title}
          price={price}
          compareAtPrice={compareAtPrice}
          fractionalPrices={false}
        >
          <Text>{selectedOptions}</Text>
        </ProductCardSmall>
      </ProductCell>
      <QuantityCell $fetching={fetching}>
        <QuantityButtons>
          <QuantityDecrementButton onClick={decrementQuantity}>
            <Text>−</Text>
          </QuantityDecrementButton>
          <Text>{line.quantity}</Text>
          <QuantityIncrementButton onClick={incrementQuantity}>
            <Text>+</Text>
          </QuantityIncrementButton>
        </QuantityButtons>
        <Spacer y={1} />
        <button onClick={remove}>
          <Text size="xs">Remove</Text>
        </button>
      </QuantityCell>
      <PriceCell $fetching={fetching}>
        <Text>{formatMoney(line.estimatedCost.subtotalAmount.amount, { fractional: false })}</Text>
        {line.discountAllocations?.map(({ discountedAmount: { amount } }, index) => (
          <Text key={index} weight="bold">
            - {formatMoney(amount, { fractional: false })}
          </Text>
        ))}
      </PriceCell>
    </Root>
  )
}

CartLine.propTypes = {
  line: PropTypes.shape({
    id: PropTypes.string,
    quantity: PropTypes.number,
    estimatedCost: PropTypes.shape({
      subtotalAmount: PropTypes.shape({
        amount: PropTypes.number,
      }),
    }),
    discountAllocations: PropTypes.arrayOf(
      PropTypes.shape({
        discountedAmount: PropTypes.shape({
          amount: PropTypes.number,
        }),
      })
    ),
    merchandise: PropTypes.shape({
      product: PropTypes.shape({
        handle: PropTypes.string,
        title: PropTypes.string,
        vendor: PropTypes.string,
        tags: PropTypes.arrayOf(PropTypes.string),
      }),
      image: PropTypes.shape({
        url: PropTypes.string,
      }),
      priceV2: PropTypes.shape({
        amount: PropTypes.number,
      }),
      compareAtPriceV2: PropTypes.shape({
        amount: PropTypes.number,
      }),
      selectedOptions: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string,
        })
      ),
    }),
  }),
}

CartLine.defaultProps = {}

export default CartLine
